/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef } from 'react'
import { PerioVoiceHighlight } from '@utils/perio-keyword.utils'
import PerioHistory from './PerioHistory.component'
import { PerioSection } from '@dataTypes/chartingFeature.types'
import { getChartHistoryRowId } from '@utils/chart-history-selection.utils'
import { validatePerioData } from '@utils/validatePerioData'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import {
  GridComponent,
  RecordClickEventArgs,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import {
  setPerioUpdated,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import PerioSectionTable from './PerioSectionTable.component'
import {
  PERIO_LOWER_TEETH,
  PERIO_UPPER_TEETH,
  perioSectionCursorProps,
} from './perio-chart-table.utils'

import { logger } from '@utils/safeLogger'

interface PerioChartProps {
  voiceCursor?: PerioVoiceHighlight
  chartIndex: number
  onViewNote: (id: string) => void
  isFullscreen: boolean
  gridRef: React.RefObject<GridComponent>
}

const PerioChart = ({
  voiceCursor,
  chartIndex,
  onViewNote,
  isFullscreen,
  gridRef,
}: PerioChartProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const cursorCell = useRef<HTMLTableCellElement>(null)
  const cursorTooth = voiceCursor?.tooth
  const cursorSection = voiceCursor?.section
  const cursorMetric = voiceCursor?.metric
  const cursorVisible =
    cursorTooth != null &&
    (isFullscreen ||
      (chartIndex === 0 ? PERIO_UPPER_TEETH : PERIO_LOWER_TEETH).includes(
        cursorTooth,
      ))
  useEffect(() => {
    if (!cursorVisible || cursorSection == null || cursorMetric == null) {
      return
    }
    // Follow the cursor cell, including when its arch becomes visible after the cursor moved.
    cursorCell.current?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    })
  }, [cursorTooth, cursorSection, cursorMetric, cursorVisible])
  const { showToothNumber, chartingSystem } = useClinicChartingPreferences()
  const { activePerioChart } = useActivePerioChart()
  const perioCharts = useAppSelector((state) => state.charting.perioCharts)
  const perioData = useMemo(() => {
    return { ...activePerioChart.perio_data }
  }, [activePerioChart])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      try {
        validatePerioData(PerioSection.Facial, perioData.facial)
        validatePerioData(PerioSection.Lingual, perioData.lingual)
      } catch (err) {
        logger.error('[Perio Validation Error]', err)
      }
    }
  }, [perioData])

  const renderSectionTable = (
    section: PerioSection,
    teeth: readonly number[],
    showDBMRow: boolean,
    showToothNumbers: boolean,
    labelAtBottom = false,
  ): React.ReactElement => {
    const cursor = perioSectionCursorProps(
      section,
      teeth,
      showToothNumbers,
      voiceCursor ?? null,
    )
    return (
      <PerioSectionTable
        section={section}
        teeth={teeth}
        sectionData={perioData[section]}
        showDBMRow={showDBMRow}
        showToothNumbers={showToothNumbers}
        labelAtBottom={labelAtBottom}
        numberingSystem={chartingSystem.tooth_numbering_system}
        highlightTooth={cursor.highlightTooth}
        cursorTooth={cursor.cursorTooth}
        cursorMetric={cursor.cursorMetric}
        cursorRef={cursorCell}
      />
    )
  }

  const handleSelectHistory = (row: RowSelectEventArgs): void => {
    const isDeselection = (row as RecordClickEventArgs).name === 'rowDeselected'
    const chartId = getChartHistoryRowId(row.data)
    const selectedChart = isDeselection
      ? null
      : perioCharts.find((chart) => chartId && chart.chart_id === chartId)
    if (selectedChart === undefined) return

    // Capture the stored chart before deferring; grid events may have no data
    // or be reused after the callback. Let selection DOM updates finish first.
    window.queueMicrotask(() => {
      // Viewing history is not an edit session — clear dirty so Save/Reset stay off.
      dispatch(setPerioUpdated(false))
      dispatch(setSelectedPerioChart(selectedChart))
    })
  }

  return (
    <div className="perio-page-container">
      {isFullscreen ? (
        <div className="perio-chart-wrapper">
          <div className="chart-table-wrapper">
            {renderSectionTable(
              'facial',
              PERIO_UPPER_TEETH,
              true,
              showToothNumber,
            )}
            {renderSectionTable('lingual', PERIO_UPPER_TEETH, false, false)}
          </div>
          <div className="chart-table-wrapper">
            {renderSectionTable(
              'lingual',
              PERIO_LOWER_TEETH,
              true,
              showToothNumber,
              true,
            )}
            {renderSectionTable(
              'facial',
              PERIO_LOWER_TEETH,
              false,
              false,
              true,
            )}
          </div>
        </div>
      ) : chartIndex === 0 ? (
        <div className="perio-chart-wrapper">
          {renderSectionTable(
            'facial',
            PERIO_UPPER_TEETH,
            true,
            showToothNumber,
          )}
          {renderSectionTable('lingual', PERIO_UPPER_TEETH, false, false)}
        </div>
      ) : (
        <div className="perio-chart-wrapper">
          <div className="chart-table-wrapper">
            {renderSectionTable(
              'lingual',
              PERIO_LOWER_TEETH,
              true,
              showToothNumber,
              true,
            )}
          </div>
          <div className="chart-table-wrapper">
            {renderSectionTable(
              'facial',
              PERIO_LOWER_TEETH,
              false,
              false,
              true,
            )}
          </div>
        </div>
      )}

      <div className="perio-history-wrapper">
        <PerioHistory
          onViewNote={onViewNote}
          onSelectHistory={handleSelectHistory}
          gridRef={gridRef}
        />
      </div>
    </div>
  )
}

export default PerioChart
